import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 10 * 1000;
const APP_CACHE_PREFIX = 'apporari-';
export const APP_BUILD_ID = process.env.REACT_APP_BUILD_ID || 'development';

interface AppManifest {
  buildId?: unknown;
}

export const isBuildMismatch = (manifest: AppManifest): boolean =>
  typeof manifest.buildId === 'string'
  && manifest.buildId.length > 0
  && manifest.buildId !== APP_BUILD_ID;

export const clearAppCaches = async (
  cacheStorage: Pick<CacheStorage, 'keys' | 'delete'> = caches,
): Promise<void> => {
  const cacheNames = await cacheStorage.keys();
  await Promise.all(
    cacheNames
      .filter((cacheName) => cacheName.startsWith(APP_CACHE_PREFIX))
      .map((cacheName) => cacheStorage.delete(cacheName)),
  );
};

interface VersionUpdateGuardProps {
  enabled?: boolean;
}

const VersionUpdateGuard: React.FC<VersionUpdateGuardProps> = ({
  enabled = process.env.NODE_ENV === 'production',
}) => {
  const [updateRequired, setUpdateRequired] = useState(false);
  const [updating, setUpdating] = useState(false);
  const checkInFlight = useRef(false);

  const checkVersion = useCallback(async () => {
    if (checkInFlight.current) return;
    checkInFlight.current = true;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/manifest.json`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal,
      });
      if (!response.ok) return;

      const manifest: AppManifest = await response.json();
      if (isBuildMismatch(manifest)) setUpdateRequired(true);
    } catch {
      // Offline o errore temporaneo: non bloccare un'app ancora utilizzabile.
    } finally {
      window.clearTimeout(timeoutId);
      checkInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    void checkVersion();
    const intervalId = window.setInterval(() => void checkVersion(), CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkVersion, enabled]);

  const updateApplication = async () => {
    setUpdating(true);
    try {
      if ('caches' in window) {
        await clearAppCaches();
      }
    } catch {
      // Il reload resta necessario anche se il browser non consente Cache Storage.
    }
    window.location.reload();
  };

  if (!updateRequired) return null;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="version-update-title"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'rgba(0, 0, 0, 0.65)',
      }}
    >
      <Paper elevation={8} sx={{ maxWidth: 420, width: '100%', p: 4, textAlign: 'center' }}>
        <Typography id="version-update-title" variant="h5" component="h1" gutterBottom>
          Aggiornamento necessario
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          È disponibile una nuova versione dell&apos;app. Aggiorna per continuare.
        </Typography>
        <Button variant="contained" size="large" onClick={() => void updateApplication()} disabled={updating}>
          {updating ? 'Aggiornamento in corso…' : 'Aggiorna ora'}
        </Button>
      </Paper>
    </Box>
  );
};

export default VersionUpdateGuard;
